import React, { useState, useMemo, useCallback } from 'react';
import { Typography, Spin, message, Breadcrumb } from 'antd';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { SearchOutlined, HomeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useCategoryArticles, useFolderArticles } from '../../hooks/useFaqCache';
import styles from './CategoryDetail.module.css';

const { Title } = Typography;

const CategoryDetail = React.memo(() => {
  const { t, i18n } = useTranslation();
  const { categoryId, folderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  // URL'den parametreleri al - memoized
  const urlParams = useMemo(() => ({
    folderId: searchParams.get('folder'),
    categoryId: searchParams.get('category'),
    itemName: searchParams.get('name') || 'Category'
  }), [searchParams]);
  
  // Route parametrelerini kontrol et - memoized
  const routeParams = useMemo(() => {
    // URL'den gelen parametreleri öncelikle kullan
    const actualCategoryId = urlParams.categoryId || categoryId;
    const actualFolderId = urlParams.folderId || folderId;
    const isFolderRoute = !!(actualFolderId);
    
    console.log('🔍 Route Params Debug:', {
      urlParams,
      categoryId,
      folderId,
      actualCategoryId,
      actualFolderId,
      isFolderRoute
    });
    
    return {
      actualCategoryId,
      actualFolderId,
      isFolderRoute
    };
  }, [categoryId, folderId, urlParams]);
  
  // Cache hook'larını kullan - yeni endpoint ile
  const categoryQuery = useCategoryArticles(routeParams.actualCategoryId, i18n.language);
  const folderQuery = useFolderArticles(routeParams.actualFolderId, i18n.language);
  
  console.log('🔍 Query Debug:', {
    categoryQuery: {
      enabled: categoryQuery.isEnabled,
      isLoading: categoryQuery.isLoading,
      isError: categoryQuery.isError,
      data: categoryQuery.data
    },
    folderQuery: {
      enabled: folderQuery.isEnabled,
      isLoading: folderQuery.isLoading,
      isError: folderQuery.isError,
      data: folderQuery.data
    },
    routeParams
  });
  
  // Hangi query'yi kullanacağımızı belirle - memoized
  const activeQuery = useMemo(() => 
    routeParams.isFolderRoute ? folderQuery : categoryQuery,
    [routeParams.isFolderRoute, folderQuery, categoryQuery]
  );
  
  const { data: articlesData, isLoading, isError } = activeQuery;
  
  // Makale başlığından slug oluşturan yardımcı fonksiyon
  const createSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Özel karakterleri kaldır
      .replace(/\s+/g, '-') // Boşlukları tire ile değiştir
      .replace(/-+/g, '-') // Birden fazla tireyi tek tireye çevir
      .trim();
  };

  // Makaleleri işle - memoized (yeni endpoint yapısına göre)
  const articles = useMemo(() => {
    console.log('🔍 Articles Data Debug:', articlesData);
    
    // Yeni endpoint yapısı: articlesData.data.articles
    if (articlesData?.data?.articles && Array.isArray(articlesData.data.articles)) {
      return articlesData.data.articles.map(article => ({
        id: article.id,
        title: article.title,
        description: article.description || article.body || '',
        updated_at: article.updated_at,
        status: article.status,
        slug: createSlug(article.title)
      }));
    }
    
    // Eski endpoint yapısı: articlesData.data (array)
    if (articlesData?.data && Array.isArray(articlesData.data)) {
      return articlesData.data.map(article => ({
        id: article.id,
        title: article.title,
        description: article.description || article.body || '',
        updated_at: article.updated_at,
        status: article.status,
        slug: createSlug(article.title)
      }));
    }
    
    // Fallback: articlesData (array)
    if (Array.isArray(articlesData)) {
      return articlesData.map(article => ({
        id: article.id,
        title: article.title,
        description: article.description || article.body || '',
        updated_at: article.updated_at,
        status: article.status,
        slug: createSlug(article.title)
      }));
    }
    
    console.warn('⚠️ No valid articles data found:', articlesData);
    return [];
  }, [articlesData]);
  
  // Category bilgisini hazırla - memoized (yeni endpoint yapısına göre)
  const category = useMemo(() => {
    // Yeni endpoint'ten folder bilgisini al
    if (articlesData?.data?.folder) {
      return {
        id: routeParams.actualCategoryId,
        name: articlesData.data.folder.name,
        folderId: routeParams.actualFolderId,
        folderName: articlesData.data.folder.name,
        description: articlesData.data.folder.description
      };
    }
    
    // Fallback: URL parametrelerinden al
    return {
      id: routeParams.actualCategoryId,
      name: urlParams.itemName,
      folderId: routeParams.actualFolderId
    };
  }, [articlesData, routeParams.actualCategoryId, routeParams.actualFolderId, urlParams.itemName]);

  // Event handlers - memoized
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleSearchEnter = useCallback((e) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      // Search sayfasına yönlendir
      navigate(`/search?query=${encodeURIComponent(searchTerm.trim())}`);
    }
  }, [searchTerm, navigate]);

  const handleTopicClick = useCallback((topic) => {
    setSearchTerm(topic);
    window.location.href = `/search?query=${encodeURIComponent(topic)}`;
  }, []);

  const handleBackToFaq = useCallback(() => {
    navigate('/faq');
  }, [navigate]);

  // Common topics - memoized
  const commonTopics = useMemo(() => 
    ['account management', 'product', 'booking support', 'payment', 'sales', 'reports'],
    []
  );

  if (isLoading) {
    return (
      <div className={styles.loadingPage}>
        <Spin size="large" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.errorPage}>
        <h2>Failed to Load Category</h2>
        <p>Unable to load category articles. Please try again later.</p>
        <button 
          onClick={handleBackToFaq}
          className={styles.backButton}
        >
          Back to FAQ
        </button>
      </div>
    );
  }

  return (
    <div className={styles.categoryDetailPage}>
      {/* Header Section */}
      <div className={styles.header}>
        <div className={styles.container}>
          <Breadcrumb className={styles.breadcrumb}>
            <Breadcrumb.Item>
              <Link to="/">
                <HomeOutlined /> Home
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/faq">FAQ</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`/${i18n.language}/support/solutions`}>Solutions</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{category?.name || 'Folder'}</Breadcrumb.Item>
          </Breadcrumb>
          
          <div className={styles.headerContent}>
            <Title level={1} className={styles.pageTitle}>
              {category?.name || 'Category'}
            </Title>
            
            <div className={styles.searchBar}>
              <div className={styles.searchInput}>
                <SearchOutlined className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder={t('category.searchPlaceholder')}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyPress={handleSearchEnter}
                />
              </div>
            </div>

            <div className={styles.commonTopics}>
              <span>{t('category.commonTopics')}</span>
              {commonTopics.map((topic, index) => (
                <span 
                  key={index}
                  className={styles.topic}
                  onClick={() => handleTopicClick(topic)}
                >
                  {topic}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <div className={styles.contentContainer}>
          <div className={styles.articlesSection}>
            {articles.length > 0 ? (
              <ul className={styles.articlesList}>
                {articles.map((article) => (
                  <li key={article.id} className={styles.articleItem}>
                                      <Link 
                    to={`/${i18n.language}/support/solutions/articles/${article.id}-${article.slug}`}
                    className={styles.articleLink}
                    >
                      <span className={styles.articleTitle}>{article.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <div className={styles.noArticles}>
                <Title level={4}>
                  {routeParams.isFolderRoute ? t('category.noArticlesInFolder') : t('category.noArticlesInCategory')}
                </Title>
                <p>
                  {routeParams.isFolderRoute 
                    ? t('category.noArticlesInFolderMessage', { name: category?.name })
                    : t('category.noArticlesInCategoryMessage')
                  }
                </p>
                <div className={styles.debugInfo}>
                  <p><strong>Debug Info:</strong></p>
                  <p>Category ID: {routeParams.actualCategoryId}</p>
                  {routeParams.isFolderRoute && <p>Folder ID: {routeParams.actualFolderId}</p>}
                  <p>Language: {i18n.language}</p>
                  <p>Articles Count: {articles.length}</p>
                </div>
                <Link to="/faq" className={styles.backToFaqLink}>
                  {t('category.backToFaq')}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

CategoryDetail.displayName = 'CategoryDetail';

export default CategoryDetail; 